// Reliable "scroll to a section" used by every in-page CTA (lead/enquiry forms,
// anchor jumps). It behaves the same on desktop and mobile:
//
//  • Desktop runs Lenis smooth scroll — route through it so native scrolls don't
//    fight the inertia engine.
//  • Mobile / reduced-motion has no Lenis. The browser's `scroll-behavior: smooth`
//    is unreliable here: our pages use GSAP ScrollTrigger pins and lazy sections,
//    so the target's position shifts WHILE the smooth scroll is running and the
//    page stops at the wrong section or glitches. Instead we drive the scroll
//    ourselves and RE-MEASURE the target every frame, so pin/layout shifts are
//    tracked and we always land exactly on the form.

const HEADER_OFFSET = 90; // sticky header height so the target clears it.

type LenisLike = {
  scrollTo: (
    target: number | HTMLElement,
    opts?: { offset?: number; immediate?: boolean; force?: boolean },
  ) => void;
};

let activeRaf = 0;

function clampToDocument(y: number) {
  const max = document.documentElement.scrollHeight - window.innerHeight;
  return Math.max(0, Math.min(y, max));
}

export function scrollToElement(target: HTMLElement | null, offset: number = HEADER_OFFSET) {
  if (!target || typeof window === "undefined") {
    return;
  }

  const lenis = (window as unknown as { __lenis?: LenisLike }).__lenis;
  if (lenis) {
    lenis.scrollTo(target, { offset: -offset });
    return;
  }

  // --- No Lenis (mobile / reduced-motion): frame-driven, self-correcting scroll ---
  if (activeRaf) {
    cancelAnimationFrame(activeRaf);
    activeRaf = 0;
  }

  const root = document.documentElement;
  const prevBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto"; // stop CSS smooth-scroll from fighting us

  const goal = () => clampToDocument(target.getBoundingClientRect().top + window.scrollY - offset);

  const startY = window.scrollY;
  const distance = Math.abs(goal() - startY);
  const duration = Math.min(900, Math.max(320, distance * 0.5));
  const startedAt = performance.now();

  const cancel = () => {
    if (activeRaf) {
      cancelAnimationFrame(activeRaf);
      activeRaf = 0;
    }
    root.style.scrollBehavior = prevBehavior;
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchmove", cancel);
  };

  const step = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    // Re-read goal() each frame so a shifting layout (pins, lazy content) can't
    // strand us at the wrong spot.
    window.scrollTo(0, Math.round(startY + (goal() - startY) * eased));

    if (progress < 1) {
      activeRaf = requestAnimationFrame(step);
      return;
    }
    window.scrollTo(0, goal()); // settle exactly on the target
    root.style.scrollBehavior = prevBehavior;
    activeRaf = 0;
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchmove", cancel);
  };

  // If the user takes over mid-scroll, bail out instead of fighting them.
  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchmove", cancel, { passive: true });
  activeRaf = requestAnimationFrame(step);
}
